/**
 * The app's single full-screen file viewer, mounted once in App. Opened with
 * `openFile()` from anywhere (see `store.ts`). Shows download progress, then
 * the right stage for the file: pdf.js pages, an image, the custom video or
 * audio player, or a calm download card for anything a browser can't show.
 * Esc closes, focus is trapped, body scroll is locked, motion is gated.
 */

import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Download, ExternalLink, FileAudio, FileImage, FileQuestion, FileText, FileVideo, RotateCw, X } from "lucide-react";
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { firstFocusable, trapFocus } from "@/primitives/overlays/Modal";
import { isTopOverlay, popOverlay, pushOverlay } from "@/primitives/overlays/stack";
import { Note } from "@/primitives/surface";

import { formatSize, KIND_LABEL, type FileKind } from "./fileKinds";
import { closeViewer, downloadCurrent, retryFile, useViewerState } from "./store";
import "./viewer.css";

const ZoomStage = lazy(() => import("./ZoomStage").then((m) => ({ default: m.ZoomStage })));
const VideoPlayer = lazy(() => import("./VideoPlayer").then((m) => ({ default: m.VideoPlayer })));
const AudioPlayer = lazy(() => import("./AudioPlayer").then((m) => ({ default: m.AudioPlayer })));

const KIND_ICON: Record<FileKind, typeof FileText> = {
  pdf: FileText,
  image: FileImage,
  video: FileVideo,
  audio: FileAudio,
  other: FileQuestion,
};

function Progress({ loaded, total }: { loaded: number; total: number | null }) {
  const pct = total ? Math.min(100, Math.round((loaded / total) * 100)) : null;
  return (
    <div className="fv-center">
      <div className="fv-progress" role="status" aria-live="polite">
        <span className="fv-progress__label">Opening your file</span>
        <span className="fv-progress__bar" data-indeterminate={pct === null}>
          <span className="fv-progress__fill" style={pct === null ? undefined : { transform: `scaleX(${pct / 100})` }} />
        </span>
        <span className="fv-progress__meta">
          {pct === null ? (loaded ? formatSize(loaded) : "Starting…") : `${pct}% · ${formatSize(loaded)} of ${formatSize(total ?? 0)}`}
        </span>
      </div>
    </div>
  );
}

export function FileViewerHost() {
  const state = useViewerState();
  const reduce = useReducedMotion();
  const surfaceRef = useRef<HTMLDivElement>(null);
  const tokenRef = useRef<symbol | null>(null);
  const [saving, setSaving] = useState(false);
  const { open, request, status, file } = state;

  const isActive = useCallback(() => isTopOverlay(tokenRef.current), []);

  useEffect(() => {
    if (!open) return undefined;
    const token = pushOverlay();
    tokenRef.current = token;
    const previous = document.activeElement as HTMLElement | null;
    const overflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const t = window.setTimeout(() => {
      const el = surfaceRef.current;
      if (el) (firstFocusable(el) ?? el).focus({ preventScroll: true });
    }, 30);
    const onKey = (e: KeyboardEvent) => {
      if (!isTopOverlay(token)) return;
      if (e.key === "Escape") {
        if (document.fullscreenElement) return; // the browser leaves fullscreen first
        e.stopPropagation();
        closeViewer();
      } else if (e.key === "Tab" && surfaceRef.current) {
        trapFocus(e, surfaceRef.current);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      popOverlay(token);
      tokenRef.current = null;
      window.clearTimeout(t);
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = overflow;
      previous?.focus?.({ preventScroll: true });
    };
  }, [open]);

  const download = async () => {
    setSaving(true);
    try {
      await downloadCurrent();
    } finally {
      setSaving(false);
    }
  };

  if (typeof document === "undefined") return null;

  const kind: FileKind = file?.kind ?? "other";
  const Icon = status !== "ready" || file?.officeUnavailable ? FileText : KIND_ICON[kind];
  const meta =
    status === "loading"
      ? "Loading…"
      : status === "error"
        ? "Couldn't open"
        : file
          ? `${file.converted ? "PDF preview" : file.officeUnavailable ? "Document" : KIND_LABEL[kind]} · ${formatSize(file.blob.size)}`
          : "";

  let body: React.ReactNode = null;
  if (status === "loading") body = <Progress loaded={state.loaded} total={state.total} />;
  else if (status === "error")
    body = (
      <div className="fv-center">
        <Note
          tone="error"
          title={state.error ?? "This file couldn't load."}
          body="If it keeps happening, let us know."
          center
          action={
            <button type="button" className="fv-btn fv-btn--soft" onClick={retryFile}>
              <RotateCw size={15} strokeWidth={1.6} aria-hidden />
              Try again
            </button>
          }
        />
      </div>
    );
  else if (file && request) {
    if (kind === "pdf" || kind === "image")
      body = <ZoomStage key={file.objectUrl} kind={kind} blob={file.blob} objectUrl={file.objectUrl} name={request.name} isActive={isActive} />;
    else if (kind === "video") body = <VideoPlayer key={file.objectUrl} src={file.objectUrl} name={request.name} isActive={isActive} />;
    else if (kind === "audio")
      body = <AudioPlayer key={file.objectUrl} src={file.objectUrl} blob={file.blob} name={request.name} isActive={isActive} />;
    else
      body = (
        <div className="fv-center">
          <div className="fv-card">
            <span className="fv-card__icon" aria-hidden>
              {file.officeUnavailable ? <FileText size={26} strokeWidth={1.5} /> : <FileQuestion size={26} strokeWidth={1.5} />}
            </span>
            <span className="fv-card__name">{request.name}</span>
            <span className="fv-card__text">
              {file.officeUnavailable
                ? "A preview of this document isn't ready right now. Download it to open it on your device."
                : "This kind of file can't be previewed here. Download it to open it on your device."}
            </span>
            <button type="button" className="fv-btn fv-btn--primary" onClick={() => void download()} disabled={saving}>
              <Download size={16} strokeWidth={1.6} aria-hidden />
              Download {formatSize(file.blob.size)}
            </button>
          </div>
        </div>
      );
  }

  return createPortal(
    <AnimatePresence>
      {open && request ? (
        <motion.div
          className="fv-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: reduce ? 0 : 0.18 }}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeViewer();
          }}
        >
          <motion.div
            ref={surfaceRef}
            className="fv"
            role="dialog"
            aria-modal="true"
            aria-label={request.name}
            tabIndex={-1}
            data-kind={status === "ready" ? kind : status}
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 16, scale: 0.985 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={reduce ? { opacity: 0 } : { opacity: 0, y: 16, scale: 0.985 }}
            transition={{ duration: reduce ? 0 : 0.24, ease: [0.2, 0, 0, 1] }}
          >
            <header className="fv-bar">
              <div className="fv-bar__title">
                <span className="fv-bar__icon" aria-hidden>
                  <Icon size={16} strokeWidth={1.6} />
                </span>
                <span className="fv-bar__text">
                  <span className="fv-bar__name" title={request.name}>
                    {request.name}
                  </span>
                  <span className="fv-bar__meta">{meta}</span>
                </span>
              </div>
              <div className="fv-bar__tools">
                {request.external ? (
                  <a className="fv-btn fv-btn--ghost" href={request.external.href} target="_blank" rel="noreferrer noopener">
                    <ExternalLink size={15} strokeWidth={1.6} aria-hidden />
                    <span className="fv-btn__text">{request.external.label}</span>
                  </a>
                ) : null}
                <button
                  type="button"
                  className="fv-btn fv-btn--primary"
                  onClick={() => void download()}
                  disabled={saving || status === "loading"}
                  aria-label={`Download ${request.name}`}
                >
                  <Download size={16} strokeWidth={1.6} aria-hidden />
                  <span className="fv-btn__text">Download</span>
                </button>
                <button type="button" className="fv-btn fv-btn--icon" onClick={closeViewer} aria-label="Close">
                  <X size={18} strokeWidth={1.75} />
                </button>
              </div>
            </header>
            <div className="fv-body">
              <Suspense fallback={<Progress loaded={state.loaded} total={state.total} />}>{body}</Suspense>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body,
  );
}
